import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import styles from './main-hero.module.scss';
import {
  ItemsInRowComponent,
  IItemsInRowComponentProps
} from '../items-in-row/items-in-row';
import Arrow from '../../assets/images/cta-arrow.inline.svg';
import PulsatingArrow from '../../assets/images/cta-pulsating-arrow.inline.svg';
import AnchorLink from 'react-anchor-link-smooth-scroll';
import { videoExtension } from '../../utils/utils';
import { Button, IButtonProps } from '../button/button';
import { useActiveJobOfferts } from '../../hooks/use-active-job-offerts';

export interface IMainHeroProps {
  backgroundAsset: { url: string };
  awards?: IItemsInRowComponentProps;
  title: string;
  description: string;
  scrollBottomText?: string;
  button?: IButtonProps;
  contactApplyMail?: string;
  buttonTextNoJob?: string;
  buttonTextJob?: string;
  careerHero?: boolean;
  showArrowWithoutText?: boolean;
}

export const MainHero: FunctionComponent<IMainHeroProps> = ({
  backgroundAsset,
  awards,
  title,
  description,
  showArrowWithoutText,
  scrollBottomText,
  button,
  contactApplyMail,
  buttonTextNoJob,
  buttonTextJob,
  careerHero
}) => {
  const [formattedTitle, setFormattedTitle] = useState('');
  const assetExtension = useMemo(
    () => backgroundAsset.url.replace(/^.*\./, ''),
    [backgroundAsset.url]
  );
  const isVideo = useMemo(() => videoExtension.includes(assetExtension), [
    assetExtension
  ]);

  useEffect(() => {
    if (typeof window !== `undefined`) {
      const parser = new DOMParser();
      const htmlDoc = parser.parseFromString(title, 'text/html');
      let newNode = '';
      let num = 0;

      htmlDoc.body.childNodes.forEach((node: HTMLElement) => {
        if (node.nodeName === 'H1') {
          num++;
          num === 1
            ? (newNode += `${node.innerHTML}`)
            : (newNode += `<br/> ${node.innerHTML}`);
        }
      });

      newNode.length ? setFormattedTitle(newNode) : setFormattedTitle(title);
    } else {
      setFormattedTitle(title);
    }
  }, [title]);
  const hasJobOfferts = useActiveJobOfferts();

  const careerButton = useMemo(() => {
    return hasJobOfferts && careerHero ? (
      <AnchorLink
        offset="100"
        className={styles.scrollBottom}
        href="#job-offers"
      >
        <Button
          className={styles.button}
          border="rectangular"
          size="large"
          CTA={true}
          text={buttonTextJob}
          theme="transparent"
          transofrmUppercase={true}
        />
      </AnchorLink>
    ) : (
      <a href={`mailto:${contactApplyMail}`} className={styles.scrollBottom}>
        <Button
          className={styles.button}
          border="rectangular"
          size="large"
          CTA={true}
          text={buttonTextNoJob}
          theme="transparent"
          transofrmUppercase={true}
        />
      </a>
    );
  }, [
    buttonTextJob,
    buttonTextNoJob,
    careerHero,
    contactApplyMail,
    hasJobOfferts
  ]);

  return (
    <div
      className={styles.wrapper}
      style={{ backgroundImage: !isVideo ? `url(${backgroundAsset.url})` : '' }}
    >
      {isVideo && (
        <video
          className={styles.video}
          autoPlay
          loop
          muted
          src={backgroundAsset.url}
        ></video>
      )}
      <div className={styles.content}>
        {formattedTitle && (
          <>
            <div className={styles.awards}>
              {awards && <ItemsInRowComponent {...awards} />}
            </div>
            <div
              className={`${styles.info} ${!scrollBottomText &&
                !button &&
                styles.withPadding}`}
            >
              <h1
                className={styles.header}
                dangerouslySetInnerHTML={{
                  __html: formattedTitle
                }}
              ></h1>
              <div
                className={styles.description}
                dangerouslySetInnerHTML={{ __html: description }}
              ></div>
              {showArrowWithoutText && (
                <AnchorLink
                  offset="100"
                  className={styles.scrollBottomPulsating}
                  href="#read-more"
                >
                  <PulsatingArrow
                    className={styles.pulsatingArrow}
                    width="50"
                    height="100"
                  />
                </AnchorLink>
              )}
              {scrollBottomText && !showArrowWithoutText && (
                <AnchorLink
                  offset="100"
                  className={styles.scrollBottom}
                  href="#read-more"
                >
                  {scrollBottomText}
                  <Arrow className={styles.arrow} />
                </AnchorLink>
              )}

              {button && (
                <Button
                  className={styles.button}
                  {...button}
                  scrollToSection={
                    button.scrollToSection
                      ? `#${button.scrollToSection}`
                      : '#read-more'
                  }
                />
              )}
              {careerHero && careerButton}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
